import { TranslocoService } from '@jsverse/transloco';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnInit, ViewChild, TemplateRef, Output, EventEmitter, ViewContainerRef, OnDestroy } from '@angular/core';
import { MatTree, MatTreeNestedDataSource } from '@angular/material/tree';
import { Subscription } from 'rxjs';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { ColorThemeService, COLOR_THEME_DARK, COLOR_THEME_NAME } from 'src/app/navigation/services/color-theme-service';
import { ContextMenuService } from '../../services/context-menu-service';
import { DirectoryStateService } from '../../services/directory-state.service';

export const RECYCLING_BIN_ID = -1;

@Component({
  selector: 'app-directory-tree',
  templateUrl: './directory-tree.component.html',
  styleUrls: ['./directory-tree.component.scss'],
})
export class DirectoryTreeComponent implements OnInit, OnDestroy {
  @ViewChild('nestedTree') nestedTree!: MatTree<any>;

  @Input() rootFolder!: DirectoryDto;
  @Input() includeRecyclingBin = true;
  @Input() contextMenu!: TemplateRef<any>;
  @Input() selectedFolderId? = 0;
  @Output() folderSelected = new EventEmitter(true);
  @Output() contextMenuOpened = new EventEmitter();

  nestedTreeControl = new NestedTreeControl<DirectoryDto>((node: DirectoryDto) => node.childDirectories);
  treeDataSource: MatTreeNestedDataSource<DirectoryDto>;

  selectedFolder?: DirectoryDto;
  recyclingBinDtoFolder!: DirectoryDto;
  isDarkTheme = false;

  private themeSubscription?: Subscription;

  constructor(
    private contextMenuService: ContextMenuService,
    private viewContainerRef: ViewContainerRef,
    private translateService: TranslocoService,
    private colorThemeService: ColorThemeService,
    private stateService: DirectoryStateService
  ) {
    this.treeDataSource = new MatTreeNestedDataSource<DirectoryDto>();
    this.themeSubscription = this.colorThemeService.colorThemeSubject.subscribe(nextValue => {
      this.isDarkTheme = nextValue === COLOR_THEME_DARK;
    });
  }

  ngOnInit(): void {
    this.treeDataSource.data = [this.rootFolder];
    this.recyclingBinDtoFolder = this.getRecyclingBinFolder();

    if (this.includeRecyclingBin && this.treeDataSource) {
      this.treeDataSource.data.push(this.recyclingBinDtoFolder);
    }
    this.nestedTreeControl.dataNodes = [this.rootFolder];
    this.isDarkTheme = localStorage.getItem(COLOR_THEME_NAME) === COLOR_THEME_DARK;

    if (this.selectedFolderId) {
      if (this.selectedFolderId == 0) {
        this.setSelectedFolder(this.rootFolder);
        return;
      }
      const selectedFolder = this.findFolderById(this.rootFolder, this.selectedFolderId);
      if (selectedFolder != null) {
        this.setSelectedFolder(selectedFolder);
      }
    }

    this.stateService.getExpandedFolderIds().subscribe(expandedIds => {
      if (expandedIds.length > 0) {
        this.restoreExpansion(this.rootFolder, expandedIds);
      }
    });
  }

  private restoreExpansion(root: DirectoryDto, ids: number[]) {
    if (ids.includes(root.id)) {
      this.nestedTreeControl.expand(root);
    }
    root.childDirectories?.forEach(child => this.restoreExpansion(child, ids));
  }

  onTreeInteraction() {
    const expandedNodes = this.nestedTreeControl.expansionModel.selected;
    const ids = expandedNodes.map(node => node.id);
    this.stateService.saveExpandedFolders(ids).subscribe();
  }

  private findFolderById(directory: DirectoryDto, folderId: number): DirectoryDto | undefined {
    if (directory.id == folderId) return directory;

    for (const childDiretcory of directory.childDirectories) {
      const currentDirectory = this.findFolderById(childDiretcory, folderId);
      if (currentDirectory) return currentDirectory;
    }
    return undefined;
  }

  private getRecyclingBinFolder() {
    const recyclingBinDtoFolder = new DirectoryDto();
    recyclingBinDtoFolder.id = RECYCLING_BIN_ID;
    recyclingBinDtoFolder.name = this.translateService.translate('dataManagement.directory.recyclingBin');
    return recyclingBinDtoFolder;
  }

  hasChild(node: DirectoryDto): boolean {
    return node.childDirectories && node.childDirectories.length > 0;
  }

  openContextMenu(event: MouseEvent, folder: DirectoryDto) {
    this.setSelectedFolder(folder);
    if (this.contextMenu) {
      this.contextMenuService.openContextMenu(this.contextMenu, this.viewContainerRef, event, folder);
    }
  }

  setSelectedFolder(folder: DirectoryDto) {
    if (!this.selectedFolder || folder.id !== this.selectedFolder.id) {
      this.selectedFolder = folder;
      this.folderSelected.emit(folder);
    }
  }

  updateDirectoryTreeData(updatedRootFolder: DirectoryDto) {
    this.rootFolder = updatedRootFolder;
    this.treeDataSource.data = [];
    let allFoldersList = [this.rootFolder];
    allFoldersList = allFoldersList.concat(this.recyclingBinDtoFolder);
    this.treeDataSource.data = allFoldersList;
    this.nestedTreeControl.dataNodes = allFoldersList;
    this.nestedTree.renderNodeChanges(this.treeDataSource.data);
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
  }
}
